import { NUXT_SITEMAP_URLS } from "./sitemap-urls";

export type SiteMapLink = {
  title: string;
  url: string;
  description?: string;
};

export type SiteMapSection = {
  title: string;
  url: string;
  links: SiteMapLink[];
};

const sectionOrder = [
  "Main Pages",
  "Notes",
  "MCQs",
  "Support Hubs",
  "Practice Mode",
  "Chapter 1 SDLC",
  "Chapter 2 Python",
  "Chapter 3 Algorithms",
  "Chapter 4 Data Structures",
  "Chapter 5 Data Analytics",
  "Chapter 6 Emerging Technologies",
  "Chapter 7 Legal & Ethics",
  "Chapter 8 Digital Literacy",
  "Chapter 9 Entrepreneurship",
] as const;

function titleCase(value: string) {
  return value
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getSectionTitle(url: string) {
  if (url === "/11th/computer/") {
    return "Main Pages";
  }

  if (url.startsWith("/11th/computer/notes")) {
    return "Notes";
  }

  if (url.startsWith("/11th/computer/mcqs")) {
    return "MCQs";
  }

  if (
    url === "/11th/computer/short-questions/"
    || url === "/11th/computer/long-questions/"
    || url === "/11th/computer/solved-exercise/"
  ) {
    return "Support Hubs";
  }

  if (url.startsWith("/11th/computer/practice")) {
    return "Practice Mode";
  }

  if (url.startsWith("/11th/computer/sdlc")) {
    return "Chapter 1 SDLC";
  }

  if (url.startsWith("/11th/computer/python")) {
    return "Chapter 2 Python";
  }

  if (url.startsWith("/11th/computer/algorithm")) {
    return "Chapter 3 Algorithms";
  }

  if (url.startsWith("/11th/computer/data-structures")) {
    return "Chapter 4 Data Structures";
  }

  if (url.startsWith("/11th/computer/data-analytics")) {
    return "Chapter 5 Data Analytics";
  }

  if (url.startsWith("/11th/computer/emerging-tech")) {
    return "Chapter 6 Emerging Technologies";
  }

  if (url.startsWith("/11th/computer/legal-ethics")) {
    return "Chapter 7 Legal & Ethics";
  }

  if (url.startsWith("/11th/computer/digital-literacy")) {
    return "Chapter 8 Digital Literacy";
  }

  if (url.startsWith("/11th/computer/entrepreneurship")) {
    return "Chapter 9 Entrepreneurship";
  }

  return "Main Pages";
}

function getLinkTitle(url: string) {
  const trimmed = url.replace(/^\/|\/$/g, "");
  const parts = trimmed.split("/");
  const relevant = parts.slice(2);

  if (url === "/11th/computer/") {
    return "11th Computer Hub";
  }

  const [first, second, third] = relevant;

  if (first === "paper-pattern") return "Paper Pattern";
  if (first === "pairing-scheme") return "Pairing Scheme";
  if (first === "important-questions") return "Important Questions";
  if (first === "guess-paper") return "Guess Paper";
  if (first === "notes") return "Computer Notes";
  if (first === "notes-pdf" && !second) return "Notes PDF Hub";
  if (first === "notes-pdf" && second) return `${titleCase(second)} Notes PDF`;
  if (first === "short-questions") return "Short Questions Hub";
  if (first === "long-questions") return "Long Questions Hub";
  if (first === "solved-exercise") return "Solved Exercise Hub";
  if (first === "practice" && !second) return "Practice Mode";
  if (first === "practice" && second) return `Practice ${titleCase(second)}`;
  if (first === "mcqs" && !second) return "MCQs Hub";
  if (first === "mcqs" && second === "top-100") return "Top 100 MCQs";
  if (first === "mcqs" && second) return titleCase(second.replace(/^chapter-\d+-/, "Chapter MCQs "));

  const unitTitles: Record<string, string> = {
    sdlc: "Chapter 1 SDLC",
    python: "Chapter 2 Python",
    algorithm: "Chapter 3 Algorithms",
    "data-structures": "Chapter 4 Data Structures",
    "data-analytics": "Chapter 5 Data Analytics",
    "emerging-tech": "Chapter 6 Emerging Technologies",
    "legal-ethics": "Chapter 7 Legal & Ethics",
    "digital-literacy": "Chapter 8 Digital Literacy",
    entrepreneurship: "Chapter 9 Entrepreneurship",
  };

  if (first && unitTitles[first]) {
    if (!second) {
      return unitTitles[first];
    }

    return `${unitTitles[first]} ${titleCase(second)}`;
  }

  if (third) {
    return `${titleCase(second)} ${titleCase(third)}`;
  }

  return titleCase(relevant.join(" "));
}

const grouped = new Map<string, SiteMapLink[]>();

for (const url of NUXT_SITEMAP_URLS) {
  const sectionTitle = getSectionTitle(url);
  const links = grouped.get(sectionTitle) ?? [];
  links.push({
    title: getLinkTitle(url),
    url,
  });
  grouped.set(sectionTitle, links);
}

export const getSiteMapSections = (): SiteMapSection[] =>
  sectionOrder
    .filter((title) => grouped.has(title))
    .map((title) => ({
      title,
      url: "/11th/computer/",
      links: grouped.get(title) ?? [],
    }));

export const SITE_MAP_URLS = getSiteMapSections()
  .flatMap((section) => section.links.map((link) => link.url));
